/* 
  total space allowed for free account is 500G, but we have a soft limit 250G
  App will show a warning when user hit 250G as well as a contact sale button
  As long as user hit 250G, the usage will be 100% and the remaining will be 0
  but user will still be able to upload until they hit the hard limit 500G.
  When the usage > 250G, instead of showing actual usage percentage, we will use >100%
*/

/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/naming-convention */
import {
  Button,
  Box,
  Link,
  formatFileSizeMetric,
} from '@ouster/webviz-react-components';

import DonutChart from './charts/DonutChart';
import styles from './UploadSpaceUsage.module.css';
import type { OrgSpaceUsage } from '../types';
import { TALK_TO_SALE_LINK } from '../utils/constants';
import { getUsageColor } from '../utils/misc';

type UploadSpaceUsageProps = {
  orgSpaceUsage: OrgSpaceUsage;
  attemptUploadSize: number;
};

export default function UploadSpaceUsage({
  orgSpaceUsage,
  attemptUploadSize,
}: UploadSpaceUsageProps) {
  const { space_used, allowed_space } = orgSpaceUsage;
  /* 
    when we fail to get space info, show usage 100
    and the usage should not exceed 100 always
  */
  const used =
    allowed_space === 0
      ? 100
      : Math.min(100, Math.round((space_used / allowed_space) * 100));

  /* 
    Since the hard limit is twice as much as soft limit, we have to double the number
    so we can visually make the soft limit looks like our hard limit
  */
  const warningLevel = used * 2 < 100 ? 0 : used * 2 < 200 ? 1 : 2;

  const donutText =
    allowed_space === 0 ? 'N/A' : used * 2 > 100 ? '>100%' : `${used * 2}%`;

  // Prevent show negative remaining number
  // from release 6, change SOFT_STORAGE_LIMIT be half of the allowed_space
  const remaining = formatFileSizeMetric(
    Math.max(0, allowed_space - space_used - allowed_space / 2)
  );
  const ringColor = getUsageColor(used * 2);

  return (
    <Box
      display="grid"
      rowGap="3rem"
      paddingTop="72px"
      justifyContent="center"
      textAlign="center"
      data-testid="upload-space-usage-root"
    >
      <h3 className={styles.title} data-testid="upload-space-usage-title">Organization Space Usage</h3>
      <Box display="grid" rowGap="2rem" data-testid="upload-space-usage-content">
        <Box
          maxHeight={{ base: '120px', lg: '200px' }}
          display="flex"
          justifyContent="center"
          data-testid="upload-space-usage-donut-container"
        >
          <DonutChart
            ringWidth={8}
            ringColor={ringColor}
            ringBgColor="var(--color-gray-3)"
            currentValue={used * 2}
            text={donutText}
            data-testid="upload-space-usage-donut"
          />
        </Box>
        <div className={styles.remaining} data-testid="upload-space-usage-remaining">
          <p>
            Remaining Storage:{' '}
            <span style={{ color: ringColor }} data-testid="upload-space-usage-remaining-value">{remaining}</span>
          </p>
          <p>
            Upload File Size:{' '}
            <span data-testid="upload-space-usage-upload-size">{`${formatFileSizeMetric(attemptUploadSize)}`}</span>
          </p>
        </div>

        {warningLevel === 1 && (
          <Box
            color="var(--ouster-colors-red-100)"
            maxW="28ch"
            lineHeight={1.5}
            data-testid="upload-space-usage-warning-soft"
          >
            You have used all of the available storage with your current plan.
          </Box>
        )}

        {warningLevel === 2 && (
          <Box maxW="28ch">
            <Box as="p" color="var(--ouster-colors-red-100)" lineHeight={1.5} data-testid="upload-space-usage-warning-hard">
              Your remaining storage cannot support this upload. Please contact
              Ouster Support for assistance.
            </Box>
          </Box>
        )}
        {warningLevel > 0 && (
          <Button
            width="100%"
            mt="1rem"
            variant="outline"
            colorScheme="gray"
            as={Link}
            href={TALK_TO_SALE_LINK}
            target="_blank"
            _hover={{
              textDecoration: 'none',
              color: 'var(--ouster-colors-border-action)',
              borderColor: 'var(--ouster-colors-border-action)',
            }}
            data-testid="upload-space-usage-get-more-storage"
          >
            Get More Storage
          </Button>
        )}
      </Box>
    </Box>
  );
}
